export default function initSelectTextSuggestions() : void {
    document.querySelectorAll<SuggestionListElement>('.js-select-text-suggestions').forEach($input => {
        new SelectTextSuggestions($input);
    });
}

export interface SuggestionListElement extends HTMLInputElement {
    $suggestionsList: HTMLUListElement;
}

/**
 * Add a suggestion list into input text
 * 
 * Usage:
 * <input type="text" class="js-select-text-suggestions" data-select-text-suggestions-values="item_1,item_2" />
 * 
 * @property {SuggestionListElement} $input           The input element
 * @property {string[]}         values           The suggestions
 * @property {HTMLUListElement} $suggestionsList The suggestions list element
 * @property {number}           winW             The window width
 */
class SelectTextSuggestions {
    $input: SuggestionListElement;
    $suggestionsList: HTMLUListElement = document.createElement('ul');
    values: string[];
    winW: number;

    constructor($input: SuggestionListElement) {
        this.$input = $input;
        this.values = $input.dataset.selectTextSuggestionsValues ? $input.dataset.selectTextSuggestionsValues.split(',') : [];
        this.winW = 0;

        if (this.values.length > 0) {
            this.createSuggestionsList();

            // Show the suggestions list element when focus the input and the input is empty
            this.$input.addEventListener('focus', () => {
                // Hide all others suggestions list elements on the page
                this.hideOthersSuggestions();

                if (this.$input.value.trim().length === 0) {
                    this.showSuggestions();
                }
            });
    
            // Hide the suggestions list element when blur the input
            this.$input.addEventListener('blur', () => {
                setTimeout(() => { // Timeout to keep the click event on the suggestions list element
                    this.hideSuggestions();
                }, 200);
            });

            // Hide the suggestions list element when clicking outside it or the input element
            document.addEventListener('click', evt => {
                if (!(evt.target as HTMLElement).closest('.js-select-text-suggestions, .select-text-suggestions')) {
                    this.hideSuggestions();
                }
            });

            this.bindKeyPressListener();
            this.bindSuggestionsClickListener();
        }
    }

    /**
     * Create the suggestions list element
     * 
     * @return {void}
     */
    createSuggestionsList() : void {
        const $suggestionsList = document.createElement('ul');
        $suggestionsList.classList.add('select-text-suggestions', 'hidden');

        this.values.forEach(value => {
            const $suggestionsItem = document.createElement('li');
            $suggestionsItem.classList.add('select-text-suggestions-item');
            $suggestionsItem.innerText = value;

            $suggestionsList.appendChild($suggestionsItem);
        });

        this.$suggestionsList = $suggestionsList;
        this.$input.$suggestionsList = this.$suggestionsList;

        document.body.appendChild($suggestionsList);
    }

    /**
     * Show the suggestions list element
     * 
     * @return {void}
     */
    showSuggestions() : void {
        // If the window width has changed,
        // recalc the suggestion list element position in the page
        if (window.innerWidth !== this.winW) {
            this.winW = window.innerWidth;

            const inputComputedStyle = window.getComputedStyle(this.$input);
            const inputBoudingClientRect = this.$input.getBoundingClientRect();

            const borderRadiusWidth = parseFloat(inputComputedStyle.borderRadius);
            const borderBottomWidth = parseFloat(inputComputedStyle.borderBottomWidth);

            const posTop = inputBoudingClientRect.y + document.documentElement.scrollTop +
                this.$input.offsetHeight -
                borderBottomWidth -
                borderRadiusWidth;
            const posLeft = inputBoudingClientRect.x;
            const inputWidth = this.$input.offsetWidth;

            this.$suggestionsList.style.top = posTop + 'px';
            this.$suggestionsList.style.left = posLeft + 'px';
            this.$suggestionsList.style.width = inputWidth + 'px';
        }

        this.$suggestionsList.classList.remove('hidden');
    }

    /**
     * Hide the suggestions list element
     * 
     * @return {void}
     */
    hideSuggestions() : void {
        this.$suggestionsList.classList.add('hidden');
    }

    /**
     * Hide all other suggestions list elements
     * 
     * @return {void}
     */
    hideOthersSuggestions() : void {
        document.querySelectorAll('.select-text-suggestions').forEach($suggestionsList => {
            if ($suggestionsList !== this.$suggestionsList) {
                $suggestionsList.classList.add('hidden');
            }
        });
    }

    /**
     * Hide the suggestions list element when pressing the "Escape" key
     * Show the suggestions list element when pressing the "ArrowUp" or "ArrowDown" keys
     * Navigate into the suggestions list element when pressing the "ArrowUp" or "ArrowDown" keys
     * Select a suggestion when pressing the "Enter" key
     * 
     * @return {void}
     */
    bindKeyPressListener() : void {
        this.$input.addEventListener('keydown', evt => {
            // Do nothing if the pressing key is not "Escape", "Enter", "ArrowUp", or "ArrowDown"
            if (!['Escape', 'Enter', 'ArrowUp', 'ArrowDown'].includes(evt.key)) {
                return;
            }

            if (evt.key === 'Escape') {
                // Close the suggestions list when pressing "Escape" key
                this.hideSuggestions();
                return;
            }

            // Select the activeItem if the suggestions list is not hidden and the pressing key is "Enter"
            if (!this.$suggestionsList.classList.contains('hidden') && evt.key === 'Enter') {
                evt.preventDefault();
                const $activeItem = this.$suggestionsList.querySelector<HTMLElement>('.active');
                if ($activeItem) {
                    this.$input.value = $activeItem.innerText;
                    $activeItem.classList.remove('active');
                    this.hideSuggestions();
                }
                return;
            }

            // Do nothing if the suggestions list is hidden and the pressing key is not "ArrowUp" or "ArrowDown"
            if (this.$suggestionsList.classList.contains('hidden') && !['ArrowUp', 'ArrowDown'].includes(evt.key)) {
                return;
            }

            this.showSuggestions();

            const $suggestionsItems = this.$suggestionsList.childNodes as NodeListOf<HTMLElement>;

            let activeIndex = -1;

            const $activeItem = this.$suggestionsList.querySelector('.active');
            if ($activeItem) {
                activeIndex = Array.prototype.indexOf.call($suggestionsItems, $activeItem);
            }

            if (evt.key === 'ArrowDown') { // ArrowDown
                activeIndex++;
                if (activeIndex > $suggestionsItems.length - 1) { // If reached the end of the suggestions
                    activeIndex = 0;
                }
            } else if (evt.key === 'ArrowUp') { // ArrowUp
                activeIndex--;
                if (activeIndex < 0) { // If reached the begin of the sugggestions
                    activeIndex = $suggestionsItems.length - 1;
                }
            }

            evt.preventDefault();

            $suggestionsItems.forEach(($item, index) => {
                if (index === activeIndex) {
                    $item.classList.add('active');
                } else {
                    $item.classList.remove('active');
                }
            });
        });
    }

    /**
     * Select a suggestion when clicking on it
     * 
     * @return {void}
     */
    bindSuggestionsClickListener() : void {
        (this.$suggestionsList.childNodes as NodeListOf<HTMLElement>).forEach($item => {
            $item.addEventListener('click', () => {
                const $activeItem = this.$suggestionsList.querySelector('.active');
                if ($activeItem) {
                    $activeItem.classList.remove('active');
                }
                this.$input.value = $item.innerText;
                this.$input.focus();
                this.hideSuggestions();
            });
        });
    }
}
